import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import type { TenantThemeConfig } from '../types/theme'
import DepositPageHeader from '../components/DepositPageHeader'
import * as authService from '../services/authService'
import * as walletService from '../services/walletService'
import { getDepositFlowConfig, type DepositFlowConfig } from '../services/depositFlowService'
import {
  pendingDepositDraftService,
  type PendingDepositMethod,
  type PendingDepositSenderAccount,
} from '../services/pendingDepositDraftService'
import {
  getDepositAccountSourceMode,
  getPublicDepositAccounts,
  isPromptPayDepositAccount,
  resolveDepositAccountForAmount,
  isTrueMoneyDepositAccount,
  type PublicDepositAccount,
} from '../services/depositAccountService'
import {
  assertDepositResponseMatchesDraft,
  createDeposit,
  getTransactionDetails,
  refreshPaymentGatewayTransaction,
  type WalletDepositResponse,
} from '../services/walletTransactionService'
import { buildPromptPayQrValue } from '../utils/promptPayQr'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface ModernTopupConfirmPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

const DEFAULT_DEPOSIT_FLOW: DepositFlowConfig = {
  tenantId: '',
  tenantName: '',
  paymentServiceSelected: false,
  paymentGatewayEnabled: false,
  slipServiceSelected: false,
  slipVerificationEnabled: false,
  effectiveDepositMode: 'manual',
  depositAutoCreditEnabled: true,
  depositAutoCreditViaPayment: true,
  depositAutoCreditViaSlip: true,
  depositAutoCreditViaSms: true,
  automationSettingsWarning: null,
}

const getMethodLabel = (method: PendingDepositMethod, paymentGatewayEnabled = false): string => {
  if (paymentGatewayEnabled && method === 'bank_transfer') return 'Payment'
  if (method === 'true_money') return 'TrueMoney Wallet'
  if (method === 'prompt_pay') return 'PromptPay'
  if (method === 'decimal_reservation') return 'ฝากทศนิยม'
  return 'โอนผ่านธนาคาร'
}

const panelStyle: React.CSSProperties = {
  background: 'linear-gradient(180deg, rgba(8,15,30,0.96) 0%, rgba(15,23,42,0.94) 100%)',
  boxShadow: '0 18px 38px rgba(15, 23, 42, 0.24)',
}

const innerSurfaceStyle: React.CSSProperties = {
  background: 'linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(241,245,249,0.96) 100%)',
  boxShadow: '0 14px 30px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
}

const valueCardStyle: React.CSSProperties = {
  ...innerSurfaceStyle,
  borderRadius: 22,
  padding: '1rem 1rem',
}

const lightSurfaceLabelStyle: React.CSSProperties = {
  color: '#64748b',
  fontWeight: 600,
}

const lightSurfaceMutedTextStyle: React.CSSProperties = {
  color: '#475569',
  fontWeight: 500,
}

const lightSurfaceHeadingStyle: React.CSSProperties = {
  color: '#0f172a',
}

const stepCardStyle = (active: boolean): React.CSSProperties => ({
  borderRadius: 22,
  padding: '0.95rem 1rem',
  border: active ? '1px solid rgba(56, 189, 248, 0.28)' : '1px solid rgba(148, 163, 184, 0.18)',
  background: active
    ? 'linear-gradient(135deg, #2563eb 0%, #38bdf8 100%)'
    : 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(226,232,240,0.92) 100%)',
  boxShadow: active ? '0 16px 30px rgba(37, 99, 235, 0.24)' : '0 10px 24px rgba(15, 23, 42, 0.08)',
})

const createDraftId = () => `deposit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const formatSecondsAsCountdown = (seconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(seconds))
  const minutes = Math.floor(safeSeconds / 60)
  const remainingSeconds = safeSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
}

const resolvePaymentTransactionAmount = (
  transaction: WalletDepositResponse['data'] | null | undefined,
  fallbackAmount: string | number = ''
): string => {
  const rawAmount = transaction?.metadata?.paymentGateway?.requestedAmount ?? transaction?.amount ?? fallbackAmount
  const numericAmount = Number(rawAmount)
  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    return String(fallbackAmount || '')
  }
  return String(numericAmount)
}

const resolvePaymentReuseExpiresAtMs = (paymentGateway: Record<string, unknown> | null | undefined): number => {
  const providerCreatedAtMs = new Date(
    String(paymentGateway?.providerCreatedAt || paymentGateway?.createdAt || '')
  ).getTime()
  const reuseWindowMinutes = Number(paymentGateway?.reuseWindowMinutes || 0)
  if (Number.isFinite(providerCreatedAtMs) && providerCreatedAtMs > 0 && Number.isFinite(reuseWindowMinutes) && reuseWindowMinutes > 0) {
    return providerCreatedAtMs + (reuseWindowMinutes * 60 * 1000)
  }

  const fallbackExpiresAtMs = new Date(String(paymentGateway?.reuseExpiresAt || '')).getTime()
  return Number.isFinite(fallbackExpiresAtMs) ? fallbackExpiresAtMs : 0
}

const buildSenderAccountSnapshot = (account?: authService.UserBankAccount | null): PendingDepositSenderAccount | undefined => {
  if (!account) return undefined
  return {
    bankCode: account.bankCode || '',
    bankName: account.bankName || '',
    accountNumber: account.accountNumber || '',
    accountName: account.accountName || '',
  }
}

const resolveLiveDepositAccount = (
  accounts: PublicDepositAccount[],
  account?: PublicDepositAccount | null
): PublicDepositAccount | null => {
  if (!account) return null
  return accounts.find((item) => item._id === account._id)
    || accounts.find((item) =>
      item.accountNumber === account.accountNumber
      && String(item.bankCode || '').toUpperCase() === String(account.bankCode || '').toUpperCase()
    )
    || null
}

const ModernTopupConfirmPage: React.FC<ModernTopupConfirmPageProps> = ({ themeConfig }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [userBankAccounts, setUserBankAccounts] = useState<authService.UserBankAccount[]>([])
  const [depositAccounts, setDepositAccounts] = useState<PublicDepositAccount[]>([])
  const [depositAccountsLoaded, setDepositAccountsLoaded] = useState(false)
  const [depositFlow, setDepositFlow] = useState<DepositFlowConfig>(DEFAULT_DEPOSIT_FLOW)
  const [flowLoaded, setFlowLoaded] = useState(false)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [paymentRefreshing, setPaymentRefreshing] = useState(false)
  const [paymentError, setPaymentError] = useState('')
  const [paymentRefreshMessage, setPaymentRefreshMessage] = useState('')
  const [paymentTransaction, setPaymentTransaction] = useState<WalletDepositResponse['data'] | null>(null)
  const [walletData, setWalletData] = useState<walletService.WalletData | null>(null)
  const [userSummary, setUserSummary] = useState<authService.UserInfoSummary | null>(null)
  const [paymentRestoreChecked, setPaymentRestoreChecked] = useState(false)
  const [countdownNowMs, setCountdownNowMs] = useState(() => Date.now())

  const currentUser = authService.getCurrentUser()
  const tenantId = currentUser?.tenantId || themeConfig.tenantId || ''
  const userId = currentUser?.id || ''
  const draftId = searchParams.get('draft')?.trim() || ''

  const restoredDraft = useMemo(() => {
    if (!tenantId || !userId || !draftId) return null
    return pendingDepositDraftService.getById(tenantId, userId, draftId)
  }, [draftId, tenantId, userId])

  const amount = String(location.state?.amount || restoredDraft?.amount || '')
  const depositAccount = (location.state?.depositAccount as PublicDepositAccount | undefined) || restoredDraft?.depositAccount
  const depositMethod = (location.state?.depositMethod as PendingDepositMethod | undefined) || restoredDraft?.depositMethod || 'bank_transfer'
  const explicitPaymentGatewaySelection =
    typeof location.state?.usesPaymentGateway === 'boolean'
      ? location.state.usesPaymentGateway
      : typeof restoredDraft?.usesPaymentGateway === 'boolean'
        ? restoredDraft.usesPaymentGateway
        : undefined
  const paymentGatewayEnabled = depositFlow.paymentGatewayEnabled === true
  const selectedUserBankAccount = userBankAccounts[0] ?? null
  const stateSenderAccount = location.state?.senderAccount as PendingDepositSenderAccount | undefined
  const selectedSenderAccount = stateSenderAccount
    || restoredDraft?.senderAccount
    || buildSenderAccountSnapshot(selectedUserBankAccount)
  const fallbackNotice = String(location.state?.fallbackNotice || '').trim()
  const amountDisplay = amount && !Number.isNaN(Number(amount)) ? `${Number(amount).toLocaleString()} บาท` : '-'
  const balanceText =
    walletData != null
      ? `${walletService.formatCurrency(walletService.getDisplayBalance(walletData))} ฿`
      : '0.00 ฿'
  const pointsText = userSummary?.points != null ? `${Number(userSummary.points).toFixed(2)} pts` : '0.00 pts'
  const userBankSummaryText = selectedUserBankAccount
    ? `${selectedUserBankAccount.accountNumber} | ${selectedUserBankAccount.bankName}`
    : 'ยังไม่ได้ผูกบัญชี'
  const isPaymentMode =
    depositMethod === 'bank_transfer' && (
      typeof explicitPaymentGatewaySelection === 'boolean'
        ? explicitPaymentGatewaySelection && (!flowLoaded || paymentGatewayEnabled)
        : (flowLoaded && paymentGatewayEnabled)
    )
  const resolvedDepositAccount = useMemo(() => {
    if (depositMethod !== 'bank_transfer') return depositAccount
    if (depositAccount && depositAccountsLoaded) {
      return resolveLiveDepositAccount(depositAccounts, depositAccount)
    }
    if (depositAccount) return depositAccount
    if (depositMethod !== 'bank_transfer' || isPaymentMode || !depositAccountsLoaded) return depositAccount

    const numericAmount = Number(amount)
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) return depositAccount

    return resolveDepositAccountForAmount(depositAccounts, numericAmount, { channel: 'bank' }) || depositAccount
  }, [amount, depositAccount, depositAccounts, depositAccountsLoaded, depositMethod, isPaymentMode])
  const isPromptPayMode = !isPaymentMode && depositMethod === 'prompt_pay'
  const isTrueMoneyMode = !isPaymentMode && depositMethod === 'true_money'
  const isDecimalReservationMode = depositMethod === 'decimal_reservation'
  const selectedDepositSourceMode = useMemo(
    () => getDepositAccountSourceMode(resolvedDepositAccount),
    [resolvedDepositAccount]
  )
  const isSmsOnlyManualDeposit = !isPaymentMode && selectedDepositSourceMode === 'sms_only'
  const isSlipOnlyManualDeposit = !isPaymentMode && selectedDepositSourceMode === 'slip_only'
  const paymentGateway = paymentTransaction?.metadata?.paymentGateway
  const activePaymentAmountValue = useMemo(
    () => resolvePaymentTransactionAmount(paymentTransaction, amount),
    [amount, paymentTransaction]
  )
  const activePaymentAmountDisplay = activePaymentAmountValue && !Number.isNaN(Number(activePaymentAmountValue))
    ? `${Number(activePaymentAmountValue).toLocaleString()} บาท`
    : amountDisplay
  const paymentQrImage = paymentGateway?.qrImage || ''
  const paymentQrRawData = paymentGateway?.qrRawData || paymentGateway?.qrcodePayload?.qrcode_text || ''
  const paymentQrLink = paymentGateway?.qrUrl || paymentGateway?.qrcodePayload?.link || ''
  const hasPaymentQr = Boolean(paymentQrImage || paymentQrRawData)
  const promptPayQrValue = useMemo(
    () => (isPromptPayMode ? buildPromptPayQrValue(resolvedDepositAccount?.accountNumber || '', amount) : null),
    [amount, isPromptPayMode, resolvedDepositAccount?.accountNumber]
  )
  const paymentExternalRefId = paymentGateway?.externalRefId || restoredDraft?.paymentExternalRefId || ''
  const paymentReuseExpiresAtMs = useMemo(() => {
    return resolvePaymentReuseExpiresAtMs(paymentGateway as Record<string, unknown> | undefined)
  }, [paymentGateway])
  const paymentReuseSecondsLeft = paymentReuseExpiresAtMs > 0
    ? Math.max(0, Math.floor((paymentReuseExpiresAtMs - countdownNowMs) / 1000))
    : 0
  const paymentReuseCountdownText = formatSecondsAsCountdown(paymentReuseSecondsLeft)
  const savedPaymentTransactionId = restoredDraft?.paymentTransactionId?.trim() || ''
  const paymentStepLabels = ['เลือกยอดฝาก', 'ชำระผ่าน Payment', 'แนบสลิป']
  const manualStepLabels =
    isPromptPayMode
      ? ['เลือกยอดฝาก', 'สแกน PromptPay', isSmsOnlyManualDeposit ? 'ส่งผ่าน SMS' : isSlipOnlyManualDeposit ? 'แนบสลิป' : 'แนบ/ข้ามสลิป']
      : isTrueMoneyMode
        ? ['เลือกยอดฝาก', 'ดูบัญชี TrueMoney', isSmsOnlyManualDeposit ? 'ส่งผ่าน SMS' : isSlipOnlyManualDeposit ? 'แนบสลิป' : 'แนบ/ข้ามสลิป']
        : isDecimalReservationMode
          ? ['เลือกยอดฝาก', 'โอนเข้าบัญชีธนาคาร', isSmsOnlyManualDeposit ? 'ส่งผ่าน SMS' : isSlipOnlyManualDeposit ? 'แนบสลิป' : 'แนบ/ข้ามสลิป']
          : ['เลือกยอดฝาก', 'สรุปรายการ', isSmsOnlyManualDeposit ? 'ส่งผ่าน SMS' : isSlipOnlyManualDeposit ? 'แนบสลิป' : 'แนบ/ข้ามสลิป']
  const hasValidPromptPayAccount = Boolean(resolvedDepositAccount && isPromptPayDepositAccount(resolvedDepositAccount))
  const hasValidTrueMoneyAccount = Boolean(resolvedDepositAccount && isTrueMoneyDepositAccount(resolvedDepositAccount))
  const hasValidManualBankAccount = Boolean(
    resolvedDepositAccount && !isPromptPayDepositAccount(resolvedDepositAccount) && !isTrueMoneyDepositAccount(resolvedDepositAccount)
  )
  const canProceedToPaymentSlip =
    Boolean(paymentTransaction?._id)
    && !paymentLoading
    && !paymentRefreshing
    && !paymentError
    && hasPaymentQr

  useEffect(() => {
    if (paymentReuseSecondsLeft <= 0) return
    const intervalId = window.setInterval(() => setCountdownNowMs(Date.now()), 1000)
    return () => window.clearInterval(intervalId)
  }, [paymentReuseSecondsLeft])
  const handleCopyAccountNumber = useCallback((accountNumber?: string | null) => {
    if (!accountNumber) return
    navigator.clipboard?.writeText(accountNumber).catch(() => {})
  }, [])

  const refreshExistingPayment = useCallback(
    async (transactionId: string, options?: { silent?: boolean }) => {
      if (!transactionId) return

      if (!options?.silent) {
        setPaymentRefreshMessage('')
      }

      setPaymentRefreshing(true)
      try {
        const result = await refreshPaymentGatewayTransaction(transactionId)

        if (!result.success || !result.data?.transaction) {
          throw new Error(result.message || 'รีเฟรชข้อมูล Payment ไม่สำเร็จ')
        }

        const refreshedTransaction = result.data.transaction as NonNullable<WalletDepositResponse['data']>
        setPaymentTransaction(refreshedTransaction)

        if (tenantId && userId && draftId) {
          const refreshedAmount = resolvePaymentTransactionAmount(refreshedTransaction, amount)
          pendingDepositDraftService.upsert(tenantId, userId, {
            id: draftId,
            amount: refreshedAmount,
            depositMethod,
            depositAccount: resolvedDepositAccount || undefined,
            senderAccount: selectedSenderAccount,
            usesPaymentGateway: true,
            paymentTransactionId: refreshedTransaction._id,
            paymentExternalRefId: refreshedTransaction.metadata?.paymentGateway?.externalRefId || '',
            createdAt: restoredDraft?.createdAt,
          })
        }

        if (
          !options?.silent
          && !(
            refreshedTransaction.metadata?.paymentGateway?.qrImage
            || refreshedTransaction.metadata?.paymentGateway?.qrRawData
            || refreshedTransaction.metadata?.paymentGateway?.qrcodePayload?.qrcode_text
          )
        ) {
          setPaymentRefreshMessage('ยังไม่พบ QR ล่าสุดจากผู้ให้บริการ กรุณาลองอีกครั้งในอีกสักครู่')
        }
      } catch (error) {
        if (!options?.silent) {
          setPaymentRefreshMessage(error instanceof Error ? error.message : 'รีเฟรชข้อมูล Payment ไม่สำเร็จ')
        }
      } finally {
        setPaymentRefreshing(false)
      }
    },
    [amount, depositMethod, draftId, resolvedDepositAccount, restoredDraft, selectedSenderAccount, tenantId, userId]
  )

  const fallbackToManualBank = useCallback(
    async (response: WalletDepositResponse) => {
      if (!tenantId || !userId) return false

      const numericAmount = Number(amount)
      if (!Number.isFinite(numericAmount) || numericAmount <= 0) return false

      const availableDepositAccounts = depositAccounts.length > 0 ? depositAccounts : await getPublicDepositAccounts(tenantId)
      const fallbackDepositAccount = resolveDepositAccountForAmount(availableDepositAccounts, numericAmount, { channel: 'bank' })
      if (!fallbackDepositAccount) {
        return false
      }

      const nextDraftId = createDraftId()
      pendingDepositDraftService.upsert(tenantId, userId, {
        id: nextDraftId,
        amount: restoredDraft?.amount || String(amount),
        depositMethod: 'bank_transfer',
        depositAccount: fallbackDepositAccount,
        senderAccount: selectedSenderAccount,
        usesPaymentGateway: false,
        paymentTransactionId: '',
        paymentExternalRefId: '',
        createdAt: restoredDraft?.createdAt,
      })
      if (draftId) {
        pendingDepositDraftService.remove(tenantId, userId, draftId)
      }

      setPaymentTransaction(null)
      setPaymentError('')
      setPaymentRefreshMessage('')

      navigate(`/topup/confirm?draft=${encodeURIComponent(nextDraftId)}`, {
        replace: true,
        state: {
          amount: restoredDraft?.amount || String(amount),
          depositAccount: fallbackDepositAccount,
          depositMethod: 'bank_transfer',
          senderAccount: selectedSenderAccount,
          usesPaymentGateway: false,
          fallbackNotice: response.fallback?.userMessage || response.message || 'ระบบสลับไปใช้การฝากผ่านธนาคารแทนให้อัตโนมัติ',
        },
      })

      return true
    },
    [amount, depositAccounts, draftId, navigate, restoredDraft, tenantId, userId]
  )

  useEffect(() => {
    if (!draftId || !restoredDraft) {
      navigate('/topup', { replace: true })
      return
    }

    Promise.all([
      authService.fetchUserBankAccounts(),
      walletService.getWallet(),
      authService.fetchUserInfoSummary(),
      tenantId ? getPublicDepositAccounts(tenantId, { forceRefresh: true }) : Promise.resolve([]),
    ]).then(([accounts, walletRes, summary, publicDepositAccounts]) => {
      setUserBankAccounts(Array.isArray(accounts) ? accounts : [])
      if (walletRes.success && walletRes.data) setWalletData(walletRes.data)
      else setWalletData(null)
      setUserSummary(summary ?? null)
      setDepositAccounts(Array.isArray(publicDepositAccounts) ? publicDepositAccounts : [])
      setDepositAccountsLoaded(true)
    })

    if (!tenantId) {
      setDepositAccountsLoaded(true)
      setFlowLoaded(true)
      return
    }

    getDepositFlowConfig(tenantId)
      .then((flow) => {
        setDepositFlow(flow)
      })
      .finally(() => {
        setFlowLoaded(true)
      })
  }, [draftId, navigate, restoredDraft, tenantId])

  useEffect(() => {
    if (!flowLoaded || !restoredDraft) return
    if (!isPaymentMode && !resolvedDepositAccount && depositAccountsLoaded) {
      navigate('/topup', { replace: true })
    }
  }, [depositAccountsLoaded, flowLoaded, isPaymentMode, navigate, resolvedDepositAccount, restoredDraft])

  useEffect(() => {
    if (!flowLoaded || isPaymentMode || !resolvedDepositAccount) return

    const isInvalidPromptPay = isPromptPayMode && !hasValidPromptPayAccount
    const isInvalidTrueMoney = isTrueMoneyMode && !hasValidTrueMoneyAccount
    const isInvalidDecimalReservation = isDecimalReservationMode && !hasValidManualBankAccount

    if (!(isInvalidPromptPay || isInvalidTrueMoney || isInvalidDecimalReservation)) return

    navigate('/topup', { replace: true })
  }, [
    flowLoaded,
    hasValidManualBankAccount,
    hasValidPromptPayAccount,
    hasValidTrueMoneyAccount,
    isDecimalReservationMode,
    isPaymentMode,
    isPromptPayMode,
    isTrueMoneyMode,
    navigate,
    resolvedDepositAccount,
  ])

  useEffect(() => {
    if (!flowLoaded || !depositAccountsLoaded || !restoredDraft || isPaymentMode) return
    if (depositMethod === 'prompt_pay') return
    if (!resolvedDepositAccount) return

    const isInvalidPromptPay = isPromptPayMode && !hasValidPromptPayAccount
    const isInvalidTrueMoney = isTrueMoneyMode && !hasValidTrueMoneyAccount
    const isInvalidDecimalReservation = isDecimalReservationMode && !hasValidManualBankAccount
    if (isInvalidPromptPay || isInvalidTrueMoney || isInvalidDecimalReservation) return

    navigate(`/topup/slip?draft=${encodeURIComponent(draftId)}`, {
      replace: true,
      state: {
        amount,
        depositAccount: resolvedDepositAccount,
        depositMethod,
        senderAccount: selectedSenderAccount,
        usesPaymentGateway: false,
      },
    })
  }, [
    amount,
    depositAccountsLoaded,
    depositMethod,
    draftId,
    flowLoaded,
    hasValidManualBankAccount,
    hasValidPromptPayAccount,
    hasValidTrueMoneyAccount,
    isDecimalReservationMode,
    isPaymentMode,
    isPromptPayMode,
    isTrueMoneyMode,
    navigate,
    resolvedDepositAccount,
    restoredDraft,
    selectedSenderAccount,
  ])

  useEffect(() => {
    if (!flowLoaded) return
    if (!isPaymentMode || !tenantId || !userId || !draftId) {
      setPaymentRestoreChecked(true)
      return
    }
    if (!savedPaymentTransactionId) {
      setPaymentRestoreChecked(true)
      return
    }

    let active = true
    setPaymentRestoreChecked(false)
    setPaymentError('')
    setPaymentRefreshMessage('')

    const activeDraft = restoredDraft
    if (!activeDraft) {
      setPaymentRestoreChecked(true)
      return
    }

    getTransactionDetails(savedPaymentTransactionId)
      .then((result) => {
        if (!active) return

        if (!result.success) {
          setPaymentRefreshMessage(result.message || 'ไม่สามารถอ่านข้อมูล Payment เดิมได้ กรุณาลองรีเฟรช QR เดิมอีกครั้ง')
          return
        }

        const existingPaymentTransaction = result.data?.transaction
        const hasPaymentMetadata = Boolean(existingPaymentTransaction?.metadata?.paymentGateway)
        const canReuseExistingPayment =
          Boolean(existingPaymentTransaction?._id)
          && existingPaymentTransaction?.type === 'deposit'
          && hasPaymentMetadata
          && !['failed', 'cancelled', 'expired'].includes(String(existingPaymentTransaction?.status || '').toLowerCase())

        if (canReuseExistingPayment) {
          const restoredPaymentTransaction = existingPaymentTransaction as NonNullable<WalletDepositResponse['data']>
          setPaymentTransaction(restoredPaymentTransaction)

          const restoredGateway = restoredPaymentTransaction?.metadata?.paymentGateway
          const restoredHasQr = Boolean(
            restoredGateway?.qrImage
            || restoredGateway?.qrRawData
            || restoredGateway?.qrcodePayload?.qrcode_text
          )
          if (!restoredHasQr && restoredPaymentTransaction?._id && restoredGateway?.externalRefId) {
            void refreshExistingPayment(restoredPaymentTransaction._id, { silent: true })
          }
          return
        }

        pendingDepositDraftService.upsert(tenantId, userId, {
          id: draftId,
          amount: activeDraft.amount,
          depositMethod: activeDraft.depositMethod,
          depositAccount: activeDraft.depositAccount,
          senderAccount: activeDraft.senderAccount,
          usesPaymentGateway: activeDraft.usesPaymentGateway,
          paymentTransactionId: '',
          paymentExternalRefId: '',
          createdAt: activeDraft.createdAt,
        })
      })
      .finally(() => {
        if (active) setPaymentRestoreChecked(true)
      })

    return () => {
      active = false
    }
  }, [draftId, flowLoaded, isPaymentMode, refreshExistingPayment, restoredDraft, savedPaymentTransactionId, tenantId, userId])

  const requestPaymentQr = async () => {
    if (!tenantId || !userId || !draftId || !amount || Number(amount) <= 0) {
      setPaymentError('ข้อมูลรายการฝากไม่ครบถ้วน')
      return
    }
    if (!selectedSenderAccount?.bankCode || !selectedSenderAccount?.accountNumber) {
      setPaymentError('ไม่พบบัญชีธนาคารของผู้ใช้')
      return
    }

    setPaymentLoading(true)
    setPaymentError('')
    setPaymentRefreshMessage('')
    try {
      const result = await createDeposit({
        amount: Number(amount),
        paymentMethod: 'payment_gateway',
        reference: draftId,
        metadata: {
          note: 'user-modern created payment QR request',
          depositMethod,
          sourceChannel: 'payment',
          paymentChannel: 'PPQR',
          userMobile: currentUser?.mobile,
          userBankAccount: {
            bankCode: selectedSenderAccount.bankCode,
            bankName: selectedSenderAccount.bankName,
            accountNumber: selectedSenderAccount.accountNumber,
            accountName: selectedSenderAccount.accountName,
          },
        },
      })

      if (!result.success || !result.data) {
        if (result.fallback?.allowed && await fallbackToManualBank(result)) {
          return
        }
        throw new Error(result.message || 'สร้าง QR ชำระเงินไม่สำเร็จ')
      }

      assertDepositResponseMatchesDraft(result, draftId)
      setPaymentTransaction(result.data)
      const activeAmount = resolvePaymentTransactionAmount(result.data, amount)
      pendingDepositDraftService.upsert(tenantId, userId, {
        id: draftId,
        amount: activeAmount,
        depositMethod,
        depositAccount: resolvedDepositAccount || undefined,
        senderAccount: selectedSenderAccount,
        usesPaymentGateway: true,
        paymentTransactionId: result.data._id,
        paymentExternalRefId: result.data.metadata?.paymentGateway?.externalRefId || '',
        createdAt: restoredDraft?.createdAt,
      })
    } catch (error) {
      setPaymentTransaction(null)
      setPaymentError(error instanceof Error ? error.message : 'สร้าง QR ชำระเงินไม่สำเร็จ')
    } finally {
      setPaymentLoading(false)
    }
  }

  useEffect(() => {
    if (!flowLoaded || !paymentRestoreChecked) return
    if (savedPaymentTransactionId && !paymentTransaction) return
    if (!isPaymentMode || paymentTransaction || paymentLoading || paymentError) return
    if (!selectedSenderAccount) return
    void requestPaymentQr()
  }, [flowLoaded, isPaymentMode, paymentError, paymentLoading, paymentRestoreChecked, paymentTransaction, savedPaymentTransactionId, selectedSenderAccount])

  if (!restoredDraft) {
    return null
  }

  return (
    <div className="content">
      <div className="container">
        <div className="row">
          <div className="col-12 col-lg-10 offset-lg-1">
            <div className="row mx__1 mt-3" style={{ rowGap: '1rem' }}>
              <DepositPageHeader
                balanceText={balanceText}
                pointsText={pointsText}
                userBankSummaryText={userBankSummaryText}
              />

              <div className="col-12 px-2">
                <div className="light_sd align-items-center bdc_primary p-3 p-md-4 border_30" style={panelStyle}>
                  <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                    <div>
                        <div className="small text-white-50 mb-1">ขั้นตอนที่ 2 จาก 3</div>
                        <h4 className="c_tertiary font_md mb-1">
                          {isPaymentMode ? 'Payment' : isPromptPayMode ? 'PromptPay QR พร้อมชำระ' : 'สรุปรายการฝากเงิน'}
                        </h4>
                      <p className="text-white-50 mb-0">
                        {isPaymentMode
                          ? 'ตรวจสอบยอดและใช้ QR เพื่อชำระเงินตามจำนวนที่เลือก ก่อนแนบสลิปในขั้นตอนถัดไป'
                          : isPromptPayMode
                            ? 'สแกน QR PromptPay นี้เพื่อโอนตามยอดที่เลือก จากนั้นแนบสลิปในขั้นตอนถัดไป'
                            : isSmsOnlyManualDeposit
                              ? 'ตรวจสอบยอดฝากและบัญชีปลายทางก่อนส่งรายการ ระบบจะส่งต่อรายการแบบ SMS เท่านั้น'
                              : 'ตรวจสอบยอดฝากและบัญชีปลายทางก่อนส่งรายการฝาก'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 px-2">
                <div className="border_30 px-2 px-md-3 py-2 py-md-3" style={innerSurfaceStyle}>
                  <div className="row mx__1" style={{ rowGap: '0.75rem' }}>
                    {(isPaymentMode ? paymentStepLabels : manualStepLabels).map((label, index) => (
                      <div key={label} className="col-12 col-md-4 px-1">
                        <div
                          className={`h-100 ${index === 1 ? 'c_tertiary' : 'c_primary'}`}
                          style={stepCardStyle(index === 1)}
                        >
                          <div
                            className="small mb-1"
                            style={index === 1 ? { color: 'rgba(255,255,255,0.82)' } : lightSurfaceLabelStyle}
                          >
                            Step {index + 1}
                          </div>
                          <div className="fw-bold">{label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-5 px-2">
                <div className="light_sd align-items-center bdc_primary p-3 p-md-4 border_30 h-100" style={panelStyle}>
                  <h5 className="c_tertiary font_md mb-3">ข้อมูลรายการฝาก</h5>
                  <div className="d-grid gap-3">
                    <div style={valueCardStyle}>
                      <div className="small mb-1" style={lightSurfaceLabelStyle}>จำนวนเงิน</div>
                      <div className="c_primary" style={{ fontSize: '1.3rem', fontWeight: 800 }}>
                        {isPaymentMode ? activePaymentAmountDisplay : amountDisplay}
                      </div>
                    </div>
                    <div style={valueCardStyle}>
                      <div className="small mb-1" style={lightSurfaceLabelStyle}>ช่องทางฝาก</div>
                      <div style={{ ...lightSurfaceHeadingStyle, fontSize: '1.05rem', fontWeight: 700 }}>
                        {getMethodLabel(depositMethod, isPaymentMode)}
                      </div>
                    </div>
                    <div style={valueCardStyle}>
                      <div className="small mb-1" style={lightSurfaceLabelStyle}>บัญชีของคุณ</div>
                      <div style={{ ...lightSurfaceHeadingStyle, fontSize: '1.05rem', fontWeight: 700 }}>
                        {selectedUserBankAccount?.bankName || 'ยังไม่ได้ผูกบัญชี'}
                      </div>
                      <div className="mt-1" style={lightSurfaceMutedTextStyle}>{selectedUserBankAccount?.accountName || '-'}</div>
                      <div className="mt-2" style={{ color: '#b45309', fontWeight: 800 }}>{selectedUserBankAccount?.accountNumber || '-'}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-7 px-2">
                <div className="light_sd align-items-center bdc_primary p-3 p-md-4 border_30 h-100" style={panelStyle}>
                  <h5 className="c_tertiary font_md mb-3">
                    {isPaymentMode
                      ? 'ข้อมูลการชำระเงิน Payment'
                      : isPromptPayMode
                        ? 'PromptPay ของระบบ'
                        : isTrueMoneyMode
                          ? 'บัญชี TrueMoney ของระบบ'
                          : 'บัญชีปลายทางของระบบ'}
                  </h5>

                  {isPaymentMode ? (
                    <div className="d-grid gap-3">
                      {(paymentLoading || paymentRefreshing) && (
                        <div className="text-center" style={{ ...valueCardStyle, ...lightSurfaceMutedTextStyle }}>
                          {paymentLoading ? 'กำลังสร้าง QR ชำระเงิน...' : 'กำลังรีเฟรชข้อมูล Payment ล่าสุด...'}
                        </div>
                      )}

                      {!paymentLoading && !paymentRefreshing && paymentError && (
                        <div style={valueCardStyle}>
                          <div className="small text-danger mb-3">{paymentError}</div>
                          <button
                            type="button"
                            className="link_pmr box_row justify-content-center font_rg bdc_primary bg_primary border_30 w-100 border-0"
                            onClick={() => void requestPaymentQr()}
                          >
                            <div className="d-flex align-items-center c_tertiary font_md">
                              <h5 className="mb-0">ลองสร้าง QR ใหม่</h5>
                            </div>
                          </button>
                        </div>
                      )}

                      {!paymentLoading && !paymentRefreshing && !paymentError && (
                        <>
                          {paymentReuseSecondsLeft > 0 && (
                            <div
                              style={{
                                ...valueCardStyle,
                                border: '1px solid rgba(245, 158, 11, 0.28)',
                                background: 'linear-gradient(180deg, rgba(255,251,235,0.98) 0%, rgba(254,243,199,0.95) 100%)',
                              }}
                            >
                              <div className="small mb-1" style={{ color: '#92400e', fontWeight: 700 }}>
                                {paymentGateway?.wasReused ? 'กำลังใช้รายการ Payment เดิม' : 'บัญชีนี้มีช่วงเวลาผูกกับรายการเดิม'}
                              </div>
                              <div style={{ color: '#b45309', fontWeight: 800, fontSize: '1.2rem' }}>{paymentReuseCountdownText}</div>
                              <div className="small mt-2" style={{ color: '#78350f' }}>
                                ระบบจะ reuse รายการเดิมตามเลขบัญชีและธนาคารของผู้ใช้ เพื่อให้ตรงกับ rule ของ provider
                              </div>
                            </div>
                          )}

                          {hasPaymentQr ? (
                            <div className="text-center" style={valueCardStyle}>
                              <div className="d-inline-flex rounded-4 bg-white p-3 shadow-sm">
                                {paymentQrImage ? (
                                  <img
                                    src={paymentQrImage}
                                    alt="payment qr"
                                    className="img-fluid rounded-3"
                                    style={{ maxWidth: 280 }}
                                  />
                                ) : (
                                  <QRCodeSVG value={paymentQrRawData} size={240} level="M" includeMargin />
                                )}
                              </div>
                              <div className="small mt-3" style={lightSurfaceMutedTextStyle}>สแกน QR นี้เพื่อชำระเงินตามยอดของรายการ Payment นี้</div>
                              {paymentQrLink && (
                                <div className="small mt-2">
                                  <a href={paymentQrLink} target="_blank" rel="noreferrer">
                                    เปิดลิงก์ชำระเงิน
                                  </a>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div style={valueCardStyle}>
                              <div className="small mb-3" style={lightSurfaceMutedTextStyle}>
                                ระบบมีรายการ Payment เดิมอยู่แล้ว แต่ยังไม่มี QR ล่าสุดจากผู้ให้บริการ สามารถกดรีเฟรชเพื่อลองดึง QR เดิมกลับมาได้โดยไม่สร้างรายการใหม่
                              </div>
                              {paymentRefreshMessage && <div className="small text-warning mb-3">{paymentRefreshMessage}</div>}
                              <button
                                type="button"
                                className="link_pmr box_row justify-content-center font_rg bdc_primary bg_primary border_30 w-100 border-0"
                                disabled={!(paymentTransaction?._id || savedPaymentTransactionId || paymentExternalRefId)}
                                onClick={() => {
                                  const refreshTransactionId = paymentTransaction?._id || savedPaymentTransactionId
                                  if (!refreshTransactionId) return
                                  void refreshExistingPayment(refreshTransactionId)
                                }}
                              >
                                <div className="d-flex align-items-center c_tertiary font_md">
                                  <h5 className="mb-0">ดึง QR ล่าสุดอีกครั้ง</h5>
                                </div>
                              </button>
                            </div>
                          )}

                          <div style={valueCardStyle}>
                            <div className="small mb-1" style={lightSurfaceLabelStyle}>เลขอ้างอิง Payment</div>
                            <div className="c_primary" style={{ fontWeight: 700 }}>{paymentGateway?.externalRefId || paymentTransaction?._id || '-'}</div>
                          </div>

                          {paymentGateway?.bank && paymentGateway?.accountNumber && (
                            <div style={valueCardStyle}>
                              <div className="small mb-1" style={lightSurfaceLabelStyle}>บัญชีสำหรับชำระเงิน</div>
                              <div style={{ ...lightSurfaceHeadingStyle, fontSize: '1.05rem', fontWeight: 700 }}>{paymentGateway.bank}</div>
                              <div className="mt-2 d-flex align-items-center justify-content-between gap-2 flex-wrap">
                                <div style={{ color: '#b45309', fontWeight: 800 }}>{paymentGateway.accountNumber}</div>
                                <button
                                  type="button"
                                  className="border-0 px-3 py-2 d-inline-flex align-items-center gap-2"
                                  style={{
                                    borderRadius: 999,
                                    background: 'rgba(37, 99, 235, 0.10)',
                                    color: '#1d4ed8',
                                    fontWeight: 700,
                                  }}
                                  onClick={() => handleCopyAccountNumber(paymentGateway.accountNumber)}
                                >
                                  <i className="fa fa-copy" aria-hidden="true" />
                                  <span>Copy</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="d-grid gap-3">
                      {fallbackNotice && (
                        <div style={valueCardStyle}>
                          <div className="small" style={{ color: '#b45309', fontWeight: 700 }}>{fallbackNotice}</div>
                        </div>
                      )}
                      {isPromptPayMode && promptPayQrValue && (
                        <div className="text-center" style={valueCardStyle}>
                          <div className="small mb-3" style={lightSurfaceLabelStyle}>QR PromptPay</div>
                          <div className="d-inline-flex rounded-4 bg-white p-3 shadow-sm">
                            <QRCodeSVG value={promptPayQrValue} size={240} level="M" includeMargin />
                          </div>
                          <div className="small mt-3" style={lightSurfaceMutedTextStyle}>
                            สแกน QR นี้เพื่อโอนผ่าน PromptPay ตามยอดที่เลือก
                          </div>
                        </div>
                      )}
                      {!isPromptPayMode && (
                        <div style={valueCardStyle}>
                          <div className="small mb-1" style={lightSurfaceLabelStyle}>
                            {isTrueMoneyMode ? 'บัญชี TrueMoney ของเว็บ' : 'บัญชีปลายทางของเว็บ'}
                          </div>
                          <div style={{ ...lightSurfaceHeadingStyle, fontSize: '1.05rem', fontWeight: 700 }}>
                            {resolvedDepositAccount?.bankName || '-'}
                          </div>
                          <div className="mt-1" style={lightSurfaceMutedTextStyle}>
                            {resolvedDepositAccount?.accountName || '-'}
                          </div>
                          <div className="mt-2 d-flex align-items-center justify-content-between gap-2 flex-wrap">
                            <div style={{ color: '#b45309', fontWeight: 800 }}>
                              {resolvedDepositAccount?.accountNumber || '-'}
                            </div>
                            {resolvedDepositAccount?.accountNumber && (
                              <button
                                type="button"
                                className="border-0 px-3 py-2 d-inline-flex align-items-center gap-2"
                                style={{
                                  borderRadius: 999,
                                  background: 'rgba(37, 99, 235, 0.10)',
                                  color: '#1d4ed8',
                                  fontWeight: 700,
                                }}
                                onClick={() => handleCopyAccountNumber(resolvedDepositAccount.accountNumber)}
                              >
                                <i className="fa fa-copy" aria-hidden="true" />
                                <span>Copy</span>
                              </button>
                            )}
                          </div>
                          {isDecimalReservationMode && (
                            <div className="small mt-3" style={lightSurfaceMutedTextStyle}>
                              ฝากทศนิยมจะใช้บัญชีโอนผ่านธนาคารของระบบโดยตรง
                            </div>
                          )}
                          {!isDecimalReservationMode && selectedDepositSourceMode !== 'sms_only' && (
                            <div className="small mt-3" style={lightSurfaceMutedTextStyle}>
                              {selectedDepositSourceMode === 'slip_only'
                                ? 'บัญชีนี้กำหนดให้แนบสลิปก่อนส่งรายการทุกครั้ง'
                                : 'บัญชีนี้รองรับได้ทั้งการแนบสลิปและการรอจับคู่ผ่าน SMS'}
                            </div>
                          )}
                        </div>
                      )}
                      {depositFlow.automationSettingsWarning && (
                        <div className="small text-warning">{depositFlow.automationSettingsWarning}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-12 px-2">
                <div className="light_sd align-items-center bdc_primary p-3 border_30" style={panelStyle}>
                  <div className="d-flex flex-column flex-md-row gap-2">
                    <button type="button" className="link_pmr box_row justify-content-center font_rg bg_secondary border_30 w-100 border-0" onClick={() => navigate('/topup')}>
                      <div className="d-flex align-items-center c_secondary font_md">
                        <h5 className="mb-0">กลับไปแก้ไข</h5>
                      </div>
                    </button>

                    {isPaymentMode ? (
                      <button
                        type="button"
                        className="link_pmr box_row justify-content-center font_rg bdc_primary bg_primary border_30 w-100 border-0"
                        disabled={!canProceedToPaymentSlip}
                        onClick={() => {
                          if (!canProceedToPaymentSlip || !paymentTransaction?._id) return
                          navigate(`/topup/slip?transaction=${encodeURIComponent(paymentTransaction._id)}`, {
                            state: { amount: activePaymentAmountValue, depositMethod, usesPaymentGateway: true },
                          })
                        }}
                      >
                        <div className="d-flex align-items-center c_tertiary font_md">
                          <h5 className="mb-0">ไปขั้นตอนแนบสลิป</h5>
                        </div>
                      </button>
                    ) : isSmsOnlyManualDeposit ? (
                      <button
                        type="button"
                        className="link_pmr box_row justify-content-center font_rg bdc_primary bg_primary border_30 w-100 border-0"
                        onClick={() => navigate('/')}
                      >
                        <div className="d-flex align-items-center c_tertiary font_md">
                          <h5 className="mb-0">กลับไปหน้าแรก</h5>
                        </div>
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="link_pmr box_row justify-content-center font_rg bdc_primary bg_primary border_30 w-100 border-0"
                        onClick={() =>
                          navigate(`/topup/slip?draft=${encodeURIComponent(draftId)}`, {
                            state: { amount, depositAccount: resolvedDepositAccount, depositMethod, usesPaymentGateway: false },
                          })
                        }
                      >
                        <div className="d-flex align-items-center c_tertiary font_md">
                          <h5 className="mb-0">ไปหน้าส่งรายการฝาก</h5>
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-12 px-2 text-center">
                <Link to="/history/deposit" className="c_tertiary text-decoration-none">
                  ดูประวัติการฝากเงิน
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModernTopupConfirmPage
